import React from 'react';
import { Box, Dialog, DialogTitle, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { BoxProps, DialogProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useLanguage } from '../../contexts/LanguageContext';

export type AppDialogPresentation = 'form' | 'workflow' | 'compact' | 'confirm';

export interface AppDialogProps extends DialogProps {
  presentation: AppDialogPresentation;
  contentPadding?: 'standard' | 'none';
  footerInside?: boolean;
  closeDisabled?: boolean;
}

export interface AppDialogFormActionsProps extends BoxProps {
  edgeToEdge?: boolean;
}

const AppDialogExpandedContext = React.createContext(false);

interface AppDialogHeaderProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  subtitle?: React.ReactNode;
  id?: string;
}

/** Rich icon/title/subtitle header on full-screen dialogs; a plain title elsewhere. */
export const AppDialogHeader: React.FC<AppDialogHeaderProps> = ({ children, icon, subtitle, id }) => {
  const expanded = React.useContext(AppDialogExpandedContext);
  return (
    <DialogTitle id={id}>
      {expanded && icon ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 40, height: 40, flexShrink: 0, display: 'grid', placeItems: 'center', borderRadius: 1.5, bgcolor: 'action.selected', color: 'primary.main' }}>
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="span" variant="h6" fontWeight={700} sx={{ display: 'block', lineHeight: 1.3 }}>{children}</Typography>
            {subtitle && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</Typography>}
          </Box>
        </Box>
      ) : children}
    </DialogTitle>
  );
};

/** For forms whose submit button must stay inside the form element. */
export const AppDialogFormActions: React.FC<AppDialogFormActionsProps> = ({ edgeToEdge = true, sx, ...props }) => (
  <Box
    {...props}
    sx={[
      {
        display: 'flex',
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        justifyContent: 'flex-end',
        gap: 1,
        mt: 'auto',
        mx: edgeToEdge ? { xs: -2, sm: -3 } : 0,
        px: { xs: 2, sm: 3 },
        pt: 2,
        pb: { xs: 'calc(16px + env(safe-area-inset-bottom))', sm: 2 },
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        '& > .MuiButton-root': { width: { xs: '100%', sm: 'auto' }, m: '0 !important' },
        '& > .MuiButton-text': { border: '1px solid', borderColor: 'divider' },
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ] as SxProps<Theme>}
  />
);

/** The shared frame for dialogs opened from the app. Dialog content owns only its task-specific fields. */
const AppDialog: React.FC<AppDialogProps> = ({
  presentation,
  contentPadding = 'standard',
  footerInside = false,
  closeDisabled = false,
  fullScreen,
  PaperProps,
  onClose,
  children,
  ...props
}) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const matchMedia = React.useCallback((query: string): MediaQueryList => {
    const result = typeof window !== 'undefined' ? window.matchMedia?.(query) : undefined;
    return result ?? {
      matches: false,
      media: query,
      onchange: null,
      addListener: () => {},
      removeListener: () => {},
      addEventListener: () => {},
      removeEventListener: () => {},
      dispatchEvent: () => false,
    };
  }, []);
  const mobile = useMediaQuery(theme.breakpoints.down('sm'), { matchMedia });
  const expanded = fullScreen ?? (mobile && (presentation === 'form' || presentation === 'workflow'));
  const confirmation = presentation === 'confirm';
  const padded = contentPadding === 'standard';

  const frameStyles: SxProps<Theme> = {
    m: expanded ? 0 : { xs: 1.5, sm: 2 },
    width: expanded ? '100%' : undefined,
    maxWidth: confirmation ? 480 : undefined,
    maxHeight: expanded ? '100dvh' : 'calc(100dvh - 24px)',
    minHeight: 0,
    borderRadius: expanded ? 0 : 2,
    bgcolor: 'background.paper',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    '& > form': {
      display: 'flex',
      flexDirection: 'column',
      flex: expanded ? '1 1 auto' : undefined,
      minHeight: 0,
      overflow: 'hidden',
    },
    '& .MuiDialogTitle-root': {
      flexShrink: 0,
      px: { xs: 2, sm: 3 },
      pr: { xs: 7, sm: 8 },
      py: { xs: 2.5, sm: 2 },
      bgcolor: 'background.paper',
      backgroundImage: 'none',
      borderBottom: confirmation ? 0 : '1px solid',
      borderColor: 'divider',
      fontSize: { xs: '1.125rem', sm: '1.25rem' },
    },
    '& .MuiDialogContent-root': {
      minHeight: 0,
      overflowY: 'auto',
      px: padded ? { xs: 2, sm: 3 } : 0,
      pt: padded ? { xs: '20px !important', sm: '24px !important' } : '0 !important',
      pb: padded && !footerInside ? { xs: 2, sm: 3 } : 0,
      borderTop: 0,
      borderBottom: 0,
    },
    '& .MuiDialogActions-root': {
      flexShrink: 0,
      gap: 1,
      px: { xs: 2, sm: 3 },
      py: { xs: 1.5, sm: 2 },
      pb: { xs: 'calc(12px + env(safe-area-inset-bottom))', sm: 2 },
      borderTop: confirmation ? 0 : '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      '& > .MuiButton-text': { border: '1px solid', borderColor: 'divider' },
      ...(expanded && {
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        '& > .MuiButton-root': { width: { xs: '100%', sm: 'auto' }, m: '0 !important' },
      }),
    },
  };

  const originalSx = PaperProps?.sx;
  const paperSx = [
    ...(Array.isArray(originalSx) ? originalSx : originalSx ? [originalSx] : []),
    frameStyles,
  ] as SxProps<Theme>;

  return (
    <Dialog
      {...props}
      onClose={onClose}
      fullScreen={expanded}
      scroll="paper"
      PaperProps={{ ...PaperProps, sx: paperSx }}
    >
      <AppDialogExpandedContext.Provider value={expanded}>{children}</AppDialogExpandedContext.Provider>
      <IconButton
        aria-label={t('common.close')}
        onClick={(event) => onClose?.(event, 'escapeKeyDown')}
        disabled={closeDisabled || !onClose}
        sx={{
          position: 'absolute',
          top: { xs: 8, sm: 12 },
          right: { xs: 8, sm: 12 },
          width: 44,
          height: 44,
          zIndex: 2,
          color: 'text.secondary',
        }}
      >
        <CloseIcon />
      </IconButton>
    </Dialog>
  );
};

export default AppDialog;
